
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Users, Copy, Facebook, Twitter, MessageCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

const FloatingShareButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToast();
  const { isLoggedIn, user } = useAuth();

  // 只有登入用戶才顯示分享按鈕
  if (!isLoggedIn || !user) {
    return null;
  }

  // 生成分享連結，使用用戶ID的前8位作為邀請碼
  const generateShareUrl = () => {
    const shareCode = user.id.slice(0, 8);
    return `${window.location.origin}?share_by=${shareCode}`;
  };

  const shareUrl = generateShareUrl();
  const shareText = "我在使用這個租屋評價平台，推薦給你！";

  // 複製分享連結
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: "已複製連結",
        description: "分享連結已複製到剪貼板",
      });
    } catch (error) {
      console.error("複製失敗:", error);
      
      // 如果 clipboard API 不可用，嘗試使用舊方法
      try {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        toast({
          title: "已複製連結",
          description: "分享連結已複製到剪貼板",
        });
      } catch (fallbackError) {
        toast({
          variant: "destructive",
          title: "複製失敗",
          description: "請手動複製連結",
        });
      }
    }
  };

  // 分享到 Facebook
  const shareToFacebook = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
  };

  // 分享到 Twitter
  const shareToTwitter = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
  };

  // 分享到 LINE
  const shareToLine = () => {
    const lineUrl = `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(lineUrl, '_blank', 'width=600,height=400');
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <TooltipTrigger asChild>
              <Button
                size="lg"
                className="fixed bottom-24 right-6 h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 bg-purple-500 hover:bg-purple-600 z-40"
              >
                <Users className="h-6 w-6 text-white" />
              </Button>
            </TooltipTrigger>
          </DialogTrigger>
          
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-center">邀請朋友</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* 分享連結顯示 */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">您的專屬邀請連結：</p>
                <div className="flex items-center gap-2">
                  <input 
                    type="text" 
                    value={shareUrl} 
                    readOnly 
                    className="flex-1 text-sm bg-white border rounded px-3 py-2"
                  />
                  <Button size="sm" onClick={copyToClipboard}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* 社群媒體分享按鈕 */}
              <div className="grid grid-cols-3 gap-3">
                <Button 
                  variant="outline" 
                  onClick={shareToFacebook}
                  className="flex flex-col items-center gap-2 h-16"
                >
                  <Facebook className="h-5 w-5 text-blue-600" />
                  <span className="text-xs">Facebook</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  onClick={shareToTwitter}
                  className="flex flex-col items-center gap-2 h-16"
                >
                  <Twitter className="h-5 w-5 text-blue-400" />
                  <span className="text-xs">Twitter</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  onClick={shareToLine}
                  className="flex flex-col items-center gap-2 h-16"
                >
                  <MessageCircle className="h-5 w-5 text-green-500" />
                  <span className="text-xs">LINE</span>
                </Button>
              </div>

              {/* 說明文字 */}
              <div className="bg-purple-50 p-3 rounded-lg">
                <p className="text-sm text-purple-800">
                  💡 透過您的邀請連結，朋友點擊後您將獲得 15 積分獎勵！
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        <TooltipContent side="left" className="mb-2">
          <p>邀請朋友</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default FloatingShareButton;
